import { DayPicker, type DayPickerProps } from 'react-day-picker';
import { cn } from '@shared/lib';

export type CalendarProps = DayPickerProps;

/** Two-letter weekday labels (Figma: "Mo Tu We Th Fr Sa Su"), indexed by `Date.getDay()`. */
const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/**
 * Calendar — `react-day-picker` fully restyled with token utilities (no base CSS). Selected day
 * uses the `calendar-selected` token (Figma light-blue), week starts Monday, weekdays are the
 * Figma 2-letter labels. **A11y:** react-day-picker provides the grid roles + keyboard navigation.
 */
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  weekStartsOn = 1,
  formatters,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      weekStartsOn={weekStartsOn}
      formatters={{
        formatWeekdayName: (date) => WEEKDAY_LABELS[date.getDay()] ?? '',
        ...formatters,
      }}
      className={cn('w-fit text-fg', className)}
      classNames={{
        months: 'relative',
        month: 'space-y-3',
        month_caption: 'flex h-8 items-center justify-center',
        caption_label: 'text-sm font-medium text-fg',
        nav: 'absolute inset-x-0 top-0 flex items-center justify-between',
        button_previous:
          'inline-flex size-7 items-center justify-center rounded-md text-muted hover:bg-surface disabled:opacity-40',
        button_next:
          'inline-flex size-7 items-center justify-center rounded-md text-muted hover:bg-surface disabled:opacity-40',
        chevron: 'size-4 fill-current',
        month_grid: 'w-full border-separate [border-spacing:0_2px]',
        weekday: 'size-9 text-xs font-medium text-muted',
        day: 'size-9 p-0 text-center',
        day_button:
          'inline-flex size-full items-center justify-center rounded-md text-sm text-fg hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected:
          '[&>button]:bg-calendar-selected [&>button]:text-fg [&>button:hover]:bg-calendar-selected',
        today: '[&>button]:border [&>button]:border-border [&>button]:font-semibold',
        outside: '[&>button]:text-muted-soft',
        disabled: '[&>button]:pointer-events-none [&>button]:opacity-40',
        hidden: 'invisible',
        ...classNames,
      }}
      {...props}
    />
  );
}
